import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, switchMap, tap, throwError } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from 'src/environments/environment';
import { AuthSessionService } from './auth-session.service';

export interface ManualReportAdmin {
  uuid: string;
  email: string;
  package: string;
  vesselName: string;
  imoNumber: string;
  requestedAt: string;
  pdfAvailable: boolean;
  pdfUploadedAt: string | null;
}

export interface ManualReportsResponse {
  data: ManualReportAdmin[];
  metadata?: { page?: number; limit?: number; total?: number; totalPages?: number };
}

export interface ManualReportCustomer extends ManualReportAdmin { isManual: true; }

@Injectable({ providedIn: 'root' })
export class ManualReportsService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly authSession = inject(AuthSessionService);
  private readonly baseUrl = `${environment.baseUrl}reports`;

  listForAdmin(page = 1, limit = 20): Observable<ManualReportsResponse> {
    return this.withToken(token => this.http.get<ManualReportsResponse>(`${this.baseUrl}/manual`, {
      params: new HttpParams().set('page', page).set('limit', limit),
      headers: { Authorization: `Bearer ${token}` }
    }));
  }

  uploadPdf(uuid: string, file: File): Observable<ManualReportAdmin> {
    const validationError = this.validateFile(file);
    if (validationError) return throwError(() => validationError);
    const body = new FormData();
    body.append('file', file);
    return this.withToken(token => this.http.post<ManualReportAdmin>(`${this.baseUrl}/${encodeURIComponent(uuid)}/manual-pdf`, body, {
      headers: { Authorization: `Bearer ${token}` }
    }));
  }

  downloadPdf(uuid: string): Observable<void> {
    return this.withToken(token => this.http.get(`${this.baseUrl}/${encodeURIComponent(uuid)}/manual-pdf`, {
      headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
    })).pipe(tap(blob => this.saveBlob(blob, `manual-report-${uuid}.pdf`)), map(() => undefined));
  }

  private withToken<T>(request: (token: string) => Observable<T>): Observable<T> {
    return this.auth.getAccessTokenSilently().pipe(
      catchError(error => {
        this.authSession.handleSessionExpired();
        return throwError(() => error);
      }),
      switchMap(token => request(token))
    );
  }

  private validateFile(file: File): HttpErrorResponse | null {
    if (!file || file.type !== 'application/pdf') return this.error(400, 'The file must be a PDF.');
    if (file.size > 20 * 1024 * 1024) return this.error(413, 'The PDF exceeds the 20 MB limit.');
    return null;
  }

  private error(status: number, message: string): HttpErrorResponse {
    return new HttpErrorResponse({ status, statusText: message, error: { message } });
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
